import React, { ReactNode, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

import {
  MobileHandle,
  PopoverBackdrop,
  PopoverBody,
  PopoverCloseButton,
  PopoverHeader,
  PopoverPanel,
} from './popover.styled'

const focusableSelector =
  'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

export type PopoverProps = {
  open: boolean
  title: string
  description?: string
  closeLabel?: string
  children: ReactNode
  onClose: () => void
}

export const Popover = ({
  open,
  title,
  description,
  closeLabel = 'Zavřít',
  children,
  onClose,
}: PopoverProps) => {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return undefined
    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const frame = window.requestAnimationFrame(() => {
      const firstFocusable =
        panelRef.current?.querySelector<HTMLElement>(focusableSelector)
      ;(firstFocusable ?? panelRef.current)?.focus()
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(focusableSelector)
      )
      if (!focusable.length) {
        event.preventDefault()
        panelRef.current.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocus?.focus()
    }
  }, [open])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <PopoverBackdrop
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose()
      }}
    >
      <PopoverPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <MobileHandle aria-hidden="true" />
        <PopoverHeader>
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <PopoverCloseButton
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
          >
            ×
          </PopoverCloseButton>
        </PopoverHeader>
        <PopoverBody>{children}</PopoverBody>
      </PopoverPanel>
    </PopoverBackdrop>,
    document.body
  )
}
